import React from "react"
import type { SyntheticEvent } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"

import type { ISitePreview } from "@/models/site"
import { HighlightedSearchText } from "@/nzds/utils"
import { useSitePreviews } from "@/services/useSitePreviews/useSitePreviews"
import { useUrl } from "@/utils/url"

import SearchIcon from "@mui/icons-material/Search"
import {
  Autocomplete,
  Box,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  createFilterOptions,
} from "@mui/material"

interface Option {
  id: string
  label: string
}

const filterSiteOptions = createFilterOptions<Option>({
  trim: true,
})

const createSiteOptions = (sites: ISitePreview[] | undefined): Option[] =>
  sites?.map((site) => ({
    id: site.id,
    label: site.name,
  })) ?? []

export const SiteSearch: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { sitesPath } = useUrl()
  const { sitePreviewsData } = useSitePreviews()
  const [isSearchOpen, setIsSearchOpen] = React.useState(false)

  const handleClickAway = () => {
    setIsSearchOpen(false)
  }

  const handleSearchChange = (
    _event: SyntheticEvent,
    selectedSite: Option | null
  ) => {
    if (!selectedSite) {
      return
    }
    void navigate(`${sitesPath}/${selectedSite.id}`)
  }

  const toggleSearchInputVisibility = () => {
    setIsSearchOpen((prev) => !prev)
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box>
        {isSearchOpen ? (
          <Autocomplete
            onBlur={toggleSearchInputVisibility}
            onChange={handleSearchChange}
            openOnFocus
            filterOptions={filterSiteOptions}
            options={createSiteOptions(sitePreviewsData)}
            noOptionsText={t("siteSearch.notFound", { ns: "common" })}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                placeholder={t("siteSearch.label", { ns: "common" })}
                slotProps={{
                  ...params.slotProps,
                  htmlInput: {
                    ...params.slotProps?.htmlInput,
                    "aria-label": t("siteSearch.label", { ns: "common" }),
                  },
                  input: {
                    ...params.slotProps?.input,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                variant="outlined"
              />
            )}
            renderOption={(props, option, { inputValue }) => (
              <Box component="li" {...props}>
                <HighlightedSearchText
                  searchText={inputValue.trim()}
                  text={option.label}
                />
              </Box>
            )}
            size="small"
            sx={{
              width: 248,
            }}
          />
        ) : (
          <Tooltip title={t("siteSearch.label", { ns: "common" })}>
            <IconButton
              aria-label={t("siteSearch.label", { ns: "common" })}
              onClick={toggleSearchInputVisibility}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </ClickAwayListener>
  )
}
